import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

import { useTranslation } from 'react-i18next'

import useStyles from 'features/setting/defaults/appointment-channels/style'

import AppointmentChannelApi from 'api/setting/defaults/appointment-channel.api'

import { submitModal, unSubmitModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import SwitchButton from 'new-components/buttons/switch-button'
import { Box } from '@mui/material'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  CHANNEL_NAME: ``,
  IS_DUPLICATE_CHANNEL_NAME: ``,
  CHANNEL_NAME_STRING_BASE: ``,
  CHANNEL_NAME_STRING_EMPTY: ``,
  CHANNEL_NAME_MAXIMUM_LENGTH: ``,
  CHANNEL_NAME_ANY_REQUIRED: ``,

  CHANNEL_NAME_EN: ``,
  IS_DUPLICATE_CHANNEL_NAME_EN: ``,
  CHANNEL_NAME_EN_STRING_BASE: ``,
  CHANNEL_NAME_EN_STRING_EMPTY: ``,
  CHANNEL_NAME_EN_MAXIMUM_LENGTH: ``,
  CHANNEL_NAME_EN_ANY_REQUIRED: ``
}

export default function AppointmentChannels() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [appointmentChannels, setAppointmentChannels] = useState([])

  const [appointmentChannelId, setAppointmentChannelId] = useState(Number)
  const [channelName, setChannelName] = useState('')
  const [channelNameEn, setChannelNameEn] = useState('')
  const [channelStatus, setChannelStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>('success')
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await AppointmentChannelApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setAppointmentChannels(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ CHANNEL_NAME: t('APPOINTMENT_CHANNEL.MESSAGE.CHANNEL_NAME') } })

    dispatch(submitModal())
    AppointmentChannelApi.create(channelName, channelNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('APPOINTMENT_CHANNEL.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ CHANNEL_NAME: t(`APPOINTMENT_CHANNEL.MESSAGE.CHANNEL_NAME`) } })

    dispatch(submitModal())
    AppointmentChannelApi.update(appointmentChannelId, {
      channelName,
      channelNameEn,
      status: channelStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`APPOINTMENT_CHANNEL.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setAppointmentChannelId(0)
    setChannelName('')
    setChannelNameEn('')
    setChannelStatus('')
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      AppointmentChannelApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('APPOINTMENT_CHANNEL.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('APPOINTMENT_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      AppointmentChannelApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('APPOINTMENT_CHANNEL.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('APPOINTMENT_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { appointmentChannelId: acId, channelName: acName, channelNameEn: acNameEn, status: acStatus } = data
    setAppointmentChannelId(acId)
    setChannelName(acName)
    setChannelNameEn(acNameEn)
    setChannelStatus(acStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'channelName', disablePadding: false, label: t('APPOINTMENT_CHANNEL.TABLE.CELL.CHANNEL_NAME') },
    { id: 'channelNameEn', disablePadding: false, label: t('APPOINTMENT_CHANNEL.TABLE.CELL.CHANNEL_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('APPOINTMENT_CHANNEL.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('APPOINTMENT_CHANNEL.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentChannelId: acId, channelName: acName, channelNameEn: acNameEn, updatedBy, updatedAt, status: acStatus } = objData
    const statusBtnActive = acStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = acStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: acId,
      id: acId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: acName },
        { option: 'TEXT', align: 'left', label: acNameEn },
        { option: 'STATUS', align: 'center', label: acStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.channelName)
    setNameEn(obj.channelNameEn)
  }

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`APPOINTMENT_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`APPOINTMENT_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { channelName: name, channelNameEn: nameEn && `(${nameEn})`, status: t(`APPOINTMENT_CHANNEL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('APPOINTMENT_CHANNEL.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('APPOINTMENT_CHANNEL.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('APPOINTMENT_CHANNEL.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={appointmentChannels.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <PageModal
        title={appointmentChannelId ? t('APPOINTMENT_CHANNEL.TITLE_UPDATE') : t('APPOINTMENT_CHANNEL.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => (appointmentChannelId ? submitEdit() : submit()) }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box className="py-2">
          {appointmentChannelId ? (
            <Form className="mx-0 mt-0 mb-3 p-0 d-inline-flex w-100">
              <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
              <SwitchButton onChange={() => setChannelStatus(channelStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={channelStatus === 'ACTIVE' ? true : false} />
            </Form>
          ) : (
            ''
          )}

          <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
            <TextInput
              fullWidth
              onChange={(event) => {
                setChannelName(event.target.value)
                clearErrorMessage()
              }}
              value={channelName}
              label={t('APPOINTMENT_CHANNEL.INPUT.CHANNEL_NAME')}
              helperText={errorMessage.CHANNEL_NAME || errorMessage.IS_DUPLICATE_CHANNEL_NAME || errorMessage.CHANNEL_NAME_STRING_BASE || errorMessage.CHANNEL_NAME_STRING_EMPTY || errorMessage.CHANNEL_NAME_MAXIMUM_LENGTH || errorMessage.CHANNEL_NAME_ANY_REQUIRED}
              required={true}
            />
            <TextInput
              fullWidth
              onChange={(event) => {
                setChannelNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={channelNameEn}
              helperText={errorMessage.CHANNEL_NAME_EN || errorMessage.IS_DUPLICATE_CHANNEL_NAME_EN || errorMessage.CHANNEL_NAME_EN_STRING_BASE || errorMessage.CHANNEL_NAME_EN_STRING_EMPTY || errorMessage.CHANNEL_NAME_EN_MAXIMUM_LENGTH || errorMessage.CHANNEL_NAME_EN_ANY_REQUIRED}
              label={t('APPOINTMENT_CHANNEL.INPUT.CHANNEL_NAME_EN')}
              required={false}
            />
          </Box>
        </Box>
      </PageModal>
    </div>
  )
}
